import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, computed, inject, Signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ConfigService,
  DEFAULT_CONFIG,
  GsIconComponent,
  GsResponsiveDirective,
  ICompanyInfo,
  IConfigFooterBottombar,
  IConfigFooterInfoarea,
  IConfigFooterMenuarea,
  IInitMenu,
  ImageAccessibilityDirective,
  ImageService,
  InformationService,
  SOCIAL_MEDIA_ICON_MAP,
  StyleService,
  SystemService
} from 'libs/shared/src';
import { FooterService } from '../services/footer.service';

@Component({
  selector: 'footer-slim',
  imports: [CommonModule, NgOptimizedImage, TranslatePipe, GsIconComponent, ImageAccessibilityDirective, GsResponsiveDirective],
  templateUrl: './footer-slim.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }
  `
})
export class FooterSlimComponent {
  // Services
  protected readonly system = inject(SystemService);
  protected readonly imageService = inject(ImageService);
  protected readonly informationService = inject(InformationService);
  protected readonly styleService = inject(StyleService);
  protected readonly config = inject(ConfigService);
  protected readonly footerService = inject(FooterService);

  // References
  DEFAULT_CONFIG = DEFAULT_CONFIG;
  SOCIAL_MEDIA_ICON_MAP = SOCIAL_MEDIA_ICON_MAP;

  // Properties & Signals
  /** Footer menu items */
  footerMenu: Signal<IInitMenu[]> = computed(() => {
    const menus = this.informationService.init()?.menus;
    if (!menus) return [];
    if (this.styleService.isMobile()) {
      return menus['cs4-mobileFooter']?.length > 0 ? menus['cs4-mobileFooter'] : menus['cs4-footer']?.length > 0 ? menus['cs4-footer'] : [];
    } else {
      return menus['cs4-footer']?.length > 0 ? menus['cs4-footer'] : [];
    }
  });
  /** Info area configuration for the footer */
  configInfoarea: Signal<IConfigFooterInfoarea | undefined> = computed(() => {
    return this.config.config()?.footer?.infoarea;
  });
  /** Main area configuration for the footer */
  configMenuarea: Signal<IConfigFooterMenuarea | undefined> = computed(() => {
    return this.config.config()?.footer?.menuarea;
  });
  /** Bottom bar configuration for the footer */
  configBottombar: Signal<IConfigFooterBottombar | undefined> = computed(() => {
    return this.config.config()?.footer?.bottombar;
  });
  /** Social media links */
  socialMediaLinks: Signal<Record<string, string> | undefined> = computed(() => {
    return this.informationService.init()?.socialMediaLinks ?? {};
  });
  /** App links */
  appLinks: Signal<Record<string, string> | undefined> = computed(() => {
    return this.informationService.init()?.appLinks ?? {};
  });
  /** Company information if available, otherwise undefined. */
  companyInfo: Signal<ICompanyInfo | undefined> = computed(() => {
    return this.informationService.init()?.companyInfo;
  });
  /** CSS value for grid-template-columns based on configured column count */
  menuareaGridColumns = computed(() => {
    const cols = this.configMenuarea()?.columns ?? DEFAULT_CONFIG.footer?.menuarea?.columns ?? 3;
    return `repeat(${cols}, minmax(0, 1fr))`;
  });
  /** Whether to use container class for layout */
  useContainer = computed(() => {
    const layout = this.config.config()?.footer?.layout;
    return layout === 'LayoutContent';
  });
  /** Logo link URL */
  logoLink: Signal<string> = computed<string>(() => {
    const infoarea = this.configInfoarea();
    if (infoarea?.logo?.useHomeLinkTarget) return '/';
    if (infoarea?.logo?.linkUrl && infoarea.logo.linkUrl.length > 0) return infoarea.logo.linkUrl;
    return '/';
  });
}
